import { CheckCircle, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { InviteVisitorForm } from '@/components/InviteVisitorForm';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuth } from '@/contexts/AuthContext';
import { useResidentProperty } from '@/hooks/useResidentProperty';
import type { Invitation } from '@/types';

function SuccessScreen({
  invitation,
  onAnother,
}: {
  invitation: Invitation;
  onAnother: () => void;
}) {
  const isPendingReg = invitation.status === 'awaiting_visitor_registration';
  return (
    <div className="max-w-md mx-auto py-12 text-center space-y-6">
      <CheckCircle className="size-16 text-[var(--status-green-text)] mx-auto" />
      <div className="space-y-2">
        <h2 className="text-xl">Invitation sent!</h2>
        {isPendingReg ? (
          <>
            <p className="text-sm text-muted-foreground">
              {invitation.visitorName} has been sent an SMS with a link to register their visit.
            </p>
            <p className="text-xs text-muted-foreground">
              Their QR code and PIN will be issued once they complete registration. You&apos;ll see
              the invite below as soon as they do.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              {invitation.visitorName} will receive their invitation via SMS and email.
            </p>
            {invitation.pin && (
              <p className="text-xs text-muted-foreground">
                PIN:{' '}
                <span className="font-mono font-semibold text-foreground">{invitation.pin}</span>
              </p>
            )}
          </>
        )}
      </div>
      <div className="flex gap-3 justify-center">
        <Button onClick={onAnother} variant="outline">
          Send another
        </Button>
        <Button asChild>
          <Link to="/resident/visitors">View invitations</Link>
        </Button>
      </div>
    </div>
  );
}

export function InviteVisitorPage() {
  const { isLoading } = useAuth();
  const prop = useResidentProperty();
  const [created, setCreated] = useState<Invitation | null>(null);

  if (created) {
    return <SuccessScreen invitation={created} onAnother={() => setCreated(null)} />;
  }

  return (
    <div className="space-y-6 max-w-xl">
      <nav className="flex items-center gap-1.5 text-sm text-[var(--text-secondary)]">
        <Link
          to="/resident/visitors"
          className="hover:text-[var(--text-primary)] transition-colors"
        >
          Invitations
        </Link>
        <ChevronRight className="size-3.5 shrink-0" />
        <span className="text-[var(--text-primary)] font-medium">New Invitation</span>
      </nav>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Who&apos;s coming?</CardTitle>
          <CardDescription>
            Use this for anyone visiting once — a guest, or a one-off worker like a plumber or
            fundi. They&apos;ll get an SMS to register their visit, and their QR code is issued once
            they confirm.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ) : prop ? (
            <InviteVisitorForm
              propertyId={prop.propertyId}
              unitId={prop.unitId}
              scope="resident"
              onSuccess={setCreated}
            />
          ) : (
            <p className="text-sm text-muted-foreground py-2">
              Your account isn&apos;t linked to a unit yet. Please contact your property manager.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
